import type { AchievementUnlock } from '@/api/achievements'
import { getAchievementIcon } from '@/components/achievements/achievementIcons'
import { localizedAchievementTitle } from '@/i18n/achievementTitle'
import { useMinCopy } from '@/i18n/useUiLocale'

interface LatestAchievementBadgeProps {
  achievement: AchievementUnlock
  compact?: boolean
}

export function LatestAchievementBadge({ achievement, compact = false }: LatestAchievementBadgeProps) {
  const { dashboard, achievementTitles } = useMinCopy()
  const Icon = getAchievementIcon(achievement.icon)
  const title = localizedAchievementTitle(achievementTitles, achievement.code, achievement.title)

  if (compact) {
    return (
      <div
        className="flex min-w-[180px] flex-1 items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3"
        data-testid="latest-achievement-badge"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-700 dark:text-amber-300">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
            {dashboard.latestWin}
          </p>
          <p className="truncate text-sm font-semibold">{title}</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4"
      data-testid="latest-achievement-badge"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-700 dark:text-amber-300">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
          {dashboard.latestWin}
        </p>
        <p className="mt-0.5 text-sm font-semibold text-foreground">{title}</p>
      </div>
    </div>
  )
}
